import noop from 'lodash/noop';

import * as Actions from '../../../actions';
import { intentPlayTrack as playbackIntentPlayTrack } from '../../../actions/Playback';
import {
  playSamples as playSamplesAction,
  stopSamples as stopSamplesAction,
} from '../../../actions/SamplePlayer';
import { AudioPlayerKeys as Audio } from '../../../constants/AudioPlayer';
import { StationConstants } from '../../../constants/StationConstants';
import { loadDomains } from '../../../domains';
import { getInDomain } from '../../../domains/externalUtils';
import { tryPromise } from '../../../lib/CoreUtils';
import { getCurrentUserWebName, getCurrentUserZipCode } from '../../../lib/ListenerUtils';
import { getAdDemogCallback } from '../../../lib/PandoraApi/PandoraApiProtected';
import Logger from '../../../tools/Logger';
import * as Utils from '../../../utils';
import { disableVideoAdsForStation } from '../../Station';

const log = Logger.child({
  component: 'middleware/ExternalApi',
});

function notYetImplementedHandler({ methodName, param }) {
  log.debug(
    `External API method "${methodName}" was called with param "${param}" but is not yet implemented`,
  );
  return Promise.resolve();
}

function closeRewardsCoachmarkHandler({ param, dispatch }) {
  const reason = param && param.reason;
  return dispatch(getInDomain('playback.actions.closeRewardsCoachmark')({ reason }));
}

function startValueExchangeHandler({ param, dispatch, getState }) {
  return tryPromise(() => {
    const params = { offerName: param.offerName, ...param.rewardProperties };
    const currentStationId = getInDomain('playback.utils.getCurrentStationId')(getState());
    return dispatch(getInDomain('playback.actions.startValueExchange')(params, currentStationId));
  });
}

function playMovieHandler({ param, dispatch }) {
  return tryPromise(() => {
    const isFlexMovie = !!param.offerName;
    if (isFlexMovie) {
      let rewardProperties;
      try {
        rewardProperties = JSON.parse(param.rewardProperties);
      } catch (_e) {
        log.warn('Failed to parse JSON property "rewardProperties" for FLEX movie');
        rewardProperties = {};
      }
      return dispatch(getInDomain('playback.actions.playRewardsAd')(param, rewardProperties));
    }
    return loadDomains(['ads', 'videoAds']).then(() =>
      dispatch(getInDomain('videoAds.utils.userInitiatedVideoBegin')(param)),
    );
  });
}

export function createStationHandlerFromMusicId({ param, dispatch }) {
  return tryPromise(() => {
    let token = param.token;
    // if we have a station code already then pass it along, otherwise make it
    // a station code. It could be 'mc', 'mi', 'mf' or 'mt' coming in here, although we only use
    // mc currently to indicate the choice came from autocomplete
    /* Additionally, do not modify musicId TT0. This id needs to be passed unmodified to the service. */
    if (token !== StationConstants.THUMBPRINT_RADIO_KEY && token.indexOf('m') !== 0) {
      token = StationConstants.MUSIC_ID + token;
    }
    return dispatch(
      Actions.UseCases.startStation({
        stationCode: token,
        forceReroute: param.forceReroute,
        errorRecoveryPath: param.errorRecoveryPath,
        componentName: 'ExternalApi',
      }),
    );
  });
}

function createStationHandlerFromStationId({ param, dispatch, router, getState }) {
  return tryPromise(() => {
    // We don't need source for premium users, so if this util isn't available we know this isn't an ads listener
    // and we can pass in null for source
    const source = getInDomain(
      'audioAds.utils.getExternalAdIdentifiersForCreateStation',
      null,
    )(getState());
    return dispatch(
      Actions.UseCases.startStation({
        componentName: 'ExternalApi',
        isLaunchingSpecialStationTrack: param.isLaunchingSpecialStationTrack,
        router,
        source,
        startingTrackToken: param.startingTrackToken,
        stationId: param.token,
      }),
    );
  });
}

function launchRegistrationHandler({ dispatch }) {
  return tryPromise(() => {
    dispatch(Actions.Application.reroute(Utils.Route.getRegisterPath()));
  });
}

function launchSubscriptionHandler({ dispatch }) {
  return tryPromise(() => {
    dispatch(Actions.Application.reroute(Utils.Route.getSubscriptionPath()));
  });
}

function playTrack({ dispatch, getState }) {
  return tryPromise(() => {
    const playing = getState().audio.get(Audio.PLAYING);
    if (!playing) dispatch(playbackIntentPlayTrack({ logPlaybackActionsAnalytics: false }));
  });
}

function pauseTrack({ dispatch, getState }) {
  return tryPromise(() => {
    const playing = getState().audio.get(Audio.PLAYING);
    if (playing) dispatch(Actions.UseCases.pauseTrack({ logPlaybackActionsAnalytics: false }));
  });
}

function playOrPauseTrack({ dispatch, getState }) {
  return tryPromise(() => {
    const playing = getState().audio.get(Audio.PLAYING);
    if (playing) {
      dispatch(Actions.UseCases.pauseTrack({ logPlaybackActionsAnalytics: true }));
    } else {
      dispatch(playbackIntentPlayTrack({ logPlaybackActionsAnalytics: true }));
    }
  });
}

function pauseTrackForAd(args) {
  getInDomain('audioAds.utils.setIgnorePauseInteractions', noop)(true);
  return pauseTrack({ ...args, logPlaybackActionsAnalytics: false });
}

export function openGenrePickerHandler({ dispatch, param }) {
  return tryPromise(() => {
    dispatch(Actions.Application.reroute(Utils.Route.getBrowseGenreCategoryPath(param.category)));
  });
}

function playSamples({ param, dispatch }) {
  return tryPromise(() => {
    return dispatch(playSamplesAction(param));
  });
}

function stopSamples({ dispatch }) {
  return tryPromise(() => {
    return dispatch(stopSamplesAction());
  });
}

export function closeAdHandler({ dispatch }) {
  return tryPromise(() => {
    dispatch(getInDomain('displayAds.utils.removeDisplayAds')());
  });
}

function launchRenewalHandler({ dispatch }) {
  return tryPromise(() => {
    dispatch(Actions.Application.reroute(Utils.Route.getSubscriptionPath()));
  });
}

function declineRenewalHandler({ getState, dispatch }) {
  return tryPromise(() => {
    dispatch(Actions.Application.reroute(Utils.Route.getNowPlayingPath(getState)));
  });
}

function requestAdDemogCallbackHandler({ getState }) {
  const adDemogCallback = getAdDemogCallback();
  return (
    adDemogCallback &&
    adDemogCallback(getCurrentUserWebName(getState()), getCurrentUserZipCode(getState()))
  );
}

export function disableVideoAdsUntilNextStationChangeHandler({ dispatch }) {
  return dispatch(disableVideoAdsForStation());
}

export function launchAddVarietyHandler({ param, dispatch }) {
  return tryPromise(() => {
    dispatch(Actions.Application.reroute(Utils.Route.getStationDetailsPathForStationId(param)));
    dispatch(Actions.Station.launchAddVariety());
  });
}

/**
 * This handles 'ext_event' calls from PandoraAPI which are triggered from its
 * sendEvent method, which is called by a display ad's GPT slotRenderEnded event, or
 * can be called by display ads and some video ad templates. When ASYNC GPT is supported,
 * we can fire an action creator from the app itself to update the ad identifiers, instead of from the iframe.
 *
 * @param {string | object} context JSON object sent by Display Ads via external Pandora API eg.
 * context: { "adIdentifiers" : { "creativeId" : "0", "lineId" : "0", "advertiserId" : "0", "campaignId" : "0", "siteId" : "0", placementId" : "0" }}
 * @returns {Promise} promise that sets the new AdIdentifiers from context.
 */
export function sendEventHandler({ dispatch, param = {} }) {
  return dispatch(getInDomain('ads.utils.setExternalAdIdentifiers')(param.adIdentifiers || {}));
}

/**
 * Important: these are the actions that have been called externally
 * If you are making changes that affect the action route,
 * please update the string in our external apps (e.g. `desktop-app` repo)
 */
const allowlistActions = ['analytics.actions.logEvents'];

export function actionHandler({ dispatch, param = {} }) {
  return tryPromise(() => {
    const actionName = param.name;
    if (!allowlistActions.includes(actionName)) {
      log.warn('Invalid action called');
    } else {
      const actionData = param.data;
      dispatch(getInDomain(actionName, noop)(actionData));
    }
  });
}

function routeToNowPlaying({ dispatch }) {
  return tryPromise(() => {
    dispatch(Actions.Application.reroute('/station'));
  });
}

const methodNamesToHandlers = Object.freeze({
  ext_action: actionHandler,
  ext_add_vrty: launchAddVarietyHandler,
  ext_close_ad: closeAdHandler,
  ext_close_coachmark: closeRewardsCoachmarkHandler,
  ext_delay_ad_refresh: notYetImplementedHandler,
  ext_demog_ad: requestAdDemogCallbackHandler,
  ext_dis_video_ads: disableVideoAdsUntilNextStationChangeHandler,
  ext_event: sendEventHandler,
  ext_genre: openGenrePickerHandler,
  ext_lcswti: createStationHandlerFromStationId,
  ext_lsfi: createStationHandlerFromStationId,
  ext_lsfmi: createStationHandlerFromMusicId,
  ext_movie: playMovieHandler,
  ext_pause: pauseTrack,
  ext_pause_for_ad: pauseTrackForAd,
  ext_play: playTrack,
  ext_play_or_pause: playOrPauseTrack,
  ext_play_sample: playSamples,
  ext_stop_sample: stopSamples,
  ext_vx: startValueExchangeHandler,

  // DEPRECATED, but still handling to observe use
  ext_no_renew: declineRenewalHandler,
  ext_reg: launchRegistrationHandler,
  ext_renew: launchRenewalHandler,
  ext_route_station: routeToNowPlaying,
  ext_sub: launchSubscriptionHandler,
});

export const onExternalApiCall = (action, { dispatch, getState }) => {
  const { methodName, param, callback } = action.payload.params;
  const handler = methodNamesToHandlers[methodName];

  // @todo: how to handle cases when methodName isn't mapped?
  if (handler) {
    return handler({ methodName, param, dispatch, getState }).then(() => {
      if (callback) {
        log.debug('ExternalApi executing callback');
        callback();
      }
    });
    // @todo: how to handle errors?
  }

  log.warn(`External API method "${methodName}" called but is not defined`);
  return Promise.resolve();
};
